import { SiteFooter } from "@/components/SiteFooter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Trophy, Clock, MapPin, Monitor, Music, Beer, Users, Star, ShieldCheck, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { useReservationModal } from "@/context/ReservationContext";
import { Lightbox } from "@/components/Lightbox";
import poolBannerAsset from "@/assets/pool-room-banner.png.asset.json";
import { poolRoomBannerAlt, poolRoomBannerDescription } from "@/assets/pool-room-banner.alt";
import g25 from "@/assets/pool-gallery-25.png.asset.json";
import g26 from "@/assets/pool-gallery-26.png.asset.json";
import g27 from "@/assets/pool-gallery-27.png.asset.json";
import g28 from "@/assets/pool-gallery-28.png.asset.json";
import g29 from "@/assets/pool-gallery-29.png.asset.json";
import g30 from "@/assets/pool-gallery-30.png.asset.json";

const GALLERY: { url: string; alt: string }[] = [
  { url: g27.url, alt: "Upstairs pool tables at Plunkets Pub Dublin under the Celtic stained-glass window" },
  { url: g25.url, alt: "Two full-size pool tables in the games room at Plunkets on Middle Abbey Street, Dublin 1" },
  { url: g29.url, alt: "Friends playing pool at Plunkets Pub Dublin city centre next to the Guinness mural" },
  { url: g26.url, alt: "Wall of Games mezzanine with pool tables and stained glass at Plunkets Dublin" },
  { url: g30.url, alt: "Group enjoying a game of pool by the stained-glass window at Plunkets Dublin" },
  { url: g28.url, alt: "Tournament-grade dart board in the darts snug at Plunkets Pub Dublin" },
];
const SITE_ORIGIN = "https://plunkets.ie";
const absUrl = (u: string) => (u.startsWith("http") ? u : `${SITE_ORIGIN}${u}`);



export const Route = createFileRoute("/pooltable-dublin-citycenter")({
  head: () => {
    const ogImage = "https://plunkets.ie/og-pool.jpg?v=2";
    const url = "https://plunkets.ie/pooltable-dublin-citycenter";
    return {
      meta: [
        { title: "Pool Tables & Darts Dublin City Centre — 3 Tables, 3 Boards | Plunkets" },
        { name: "description", content: "Book a pool table or dart board in Dublin city centre. 3 pool tables and 3 dart boards at Plunkets on Middle Abbey Street — pool €12/hr · darts €15/hr, proper cues, 12 large TVs for live sport, open until 23:30 (00:30 Fri & Sat)." },
        { name: "keywords", content: "pool tables Dublin, pool table Dublin city centre, 3 pool tables Dublin, darts Dublin, dart board hire Dublin, pool pub Dublin, book pool table Dublin, sport pub Dublin" },
        { property: "og:title", content: "Pool Tables in Dublin City Centre — Plunkets" },
        { property: "og:description", content: "3 pool tables and 3 dart boards in Dublin city centre. Pool €12/hr · darts €15/hr. Book ahead or walk in." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        { property: "og:image:secure_url", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:alt", content: "Pool tables and dart boards at Plunkets Pub Dublin city centre" },
        { property: "og:locale", content: "en_IE" },
        { property: "og:site_name", content: "Plunkets Pub Dublin" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Pool Tables in Dublin City Centre — Plunkets" },
        { name: "twitter:description", content: "3 pool tables & 3 dart boards. Pool €12/hr · darts €15/hr. Book ahead or walk in." },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Pool table & dart board hire",
          name: "Pool Table & Darts Booking — Plunkets Pub Dublin",
          description: "Hire one of 3 pool tables or 3 dart boards in Dublin city centre. Pool €12/hr · darts €15/hr, open until 23:30 (00:30 Fri & Sat), 12 large TVs for live sport alongside.",
          areaServed: { "@type": "City", name: "Dublin" },
          provider: { "@id": "https://plunkets.ie/#business" },
          offers: [
            {
              "@type": "Offer",
              name: "Pool table hire (per hour)",
              price: "12",
              priceCurrency: "EUR",
              priceSpecification: { "@type": "UnitPriceSpecification", price: "12", priceCurrency: "EUR", unitCode: "HUR" },
              url,
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "Dart board hire (per hour)",
              price: "15",
              priceCurrency: "EUR",
              priceSpecification: { "@type": "UnitPriceSpecification", price: "15", priceCurrency: "EUR", unitCode: "HUR" },
              url,
              availability: "https://schema.org/InStock",
            },
          ],
        }),
      }, {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How many pool tables are at Plunkets?",
              acceptedAnswer: { "@type": "Answer", text: "Plunkets has 3 pool tables in Dublin city centre, with proper cues and lighting. Hire is €12 per hour." } },
            { "@type": "Question", name: "How many dart boards are at Plunkets?",
              acceptedAnswer: { "@type": "Answer", text: "There are 3 tournament-grade dart boards available for hire at €15 per hour." } },
            { "@type": "Question", name: "Can I watch sport while playing pool?",
              acceptedAnswer: { "@type": "Answer", text: "Yes — 12 large TVs across the pub show Premier League, GAA, hurling, World Cup and international rugby while you play." } },
            { "@type": "Question", name: "How do I book a pool table in Dublin city centre?",
              acceptedAnswer: { "@type": "Answer", text: "Reserve online from this page or walk in. Plunkets is on Middle Abbey Street, Dublin 1, open until 23:30 daily, 00:30 Fri & Sat." } },
          ],
        }),
      }, {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "Pool & Darts Gallery — Plunkets Pub Dublin",
          description: "Inside the pool room and darts snug at Plunkets Pub on Middle Abbey Street, Dublin 1 — 3 pool tables, 3 dart boards, stained-glass window and Guinness murals.",
          url: `${url}#pool-gallery-heading`,
          about: { "@id": `${SITE_ORIGIN}/#business` },
          image: GALLERY.map((g) => ({
            "@type": "ImageObject",
            contentUrl: absUrl(g.url),
            url: absUrl(g.url),
            caption: g.alt,
            description: g.alt,
            representativeOfPage: false,
            creditText: "Plunkets Pub Dublin",
            copyrightNotice: "© Plunkets Pub Dublin",
            license: `${SITE_ORIGIN}/`,
            acquireLicensePage: `${SITE_ORIGIN}/`,
          })),
        }),
      }],
    };
  },

  component: PoolPage,
});

function PoolPage() {
  const { open: openModal } = useReservationModal();
  const bannerUrl = poolBannerAsset.url;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Prefetch the neighbours of the current lightbox slide so ←/→ feels instant.
  useEffect(() => {
    if (lightboxIndex === null || typeof document === "undefined") return;
    const next = GALLERY[(lightboxIndex + 1) % GALLERY.length]?.url;
    const prev = GALLERY[(lightboxIndex - 1 + GALLERY.length) % GALLERY.length]?.url;
    const links: HTMLLinkElement[] = [];
    for (const href of [next, prev]) {
      if (!href) continue;
      const l = document.createElement("link");
      l.rel = "prefetch";
      l.as = "image";
      l.href = href;
      document.head.appendChild(l);
      links.push(l);
    }
    return () => links.forEach((l) => l.remove());
  }, [lightboxIndex]);

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Hero Banner — responsive foreground image for correct alt text + LCP, decorative background layer for cinematic framing */}
      <section
        aria-labelledby="pool-hero-title"
        className="relative flex min-h-[62vh] items-end justify-center overflow-hidden sm:min-h-[68vh] md:min-h-[72vh] lg:min-h-[78vh]"
      >
        <img
          src={bannerUrl}
          alt={poolRoomBannerAlt}
          title={poolRoomBannerAlt}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/25 sm:via-background/60 sm:to-transparent" />
        <div aria-hidden="true" className="absolute inset-0 bg-background/25 sm:bg-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-5 pb-10 pt-28 sm:px-6 sm:pb-14 sm:pt-32 md:px-8 md:pb-20 md:pt-40">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-primary transition-colors">
            <ArrowLeft size={16} /> Back to Plunkets
          </Link>
          <p className="mt-5 text-[10px] uppercase tracking-[0.35em] text-primary sm:mt-6 sm:text-xs sm:tracking-[0.4em]">3 Pool · 3 Darts</p>
          <h1
            id="pool-hero-title"
            className="mt-3 font-display text-[2.25rem] leading-[1.02] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
          >
            3 Pool Tables <span className="text-gold-gradient">&amp; 3 Dart Boards</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-white/85 sm:text-base md:text-lg">
            Three well-maintained pool tables and three tournament-grade dart boards. Walk in or book — never a long wait on a week night. Pool €12/hr · darts €15/hr.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center">
            <button
              onClick={() => openModal({ bookingType: "pool_table" })}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-lg shadow-black/30 transition-all hover:opacity-90 sm:w-auto"
            >
              Reserve a Table
            </button>
            <button
              onClick={() => openModal({ bookingType: "darts" })}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:bg-white/10 sm:w-auto"
            >
              Book Dart Boards
            </button>
          </div>
          <p className="sr-only">{poolRoomBannerDescription}</p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-5 py-12 md:px-8 md:py-16">
        <p className="max-w-2xl text-base text-muted-foreground md:text-lg">
          Plunkets is the home of pool in Dublin city centre — walk in for a frame after work or lock in a table for the weekend.
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Trophy, label: "€12–€15 / hour", sub: "Pool €12 · Darts €15 · up to 6 players" },
            { icon: Clock, label: "Open Late", sub: "23:30 daily · 00:30 Fri–Sat" },
            { icon: MapPin, label: "Middle Abbey St.", sub: "Dublin 1, city centre" },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="rounded-2xl border border-border bg-card/30 p-6">
              <Icon size={22} className="text-primary" />
              <p className="mt-4 font-display text-xl font-bold">{label}</p>
              <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
            </div>
          ))}
        </div>

        {/* Room Details */}
        <section className="mt-16">
          <p className="text-xs uppercase tracking-[0.35em] text-primary">The Setup</p>
          <h2 className="mt-2 font-display text-2xl font-bold md:text-3xl">Pool Tables &amp; Dart Boards</h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
            Our games room is built for serious play and casual fun alike — full-size slate beds, tournament wiring, and a proper pub atmosphere.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {/* Pool card */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card/40 p-6 md:p-8">
              <div className="absolute right-0 top-0 h-24 w-24 bg-gradient-to-bl from-primary/10 to-transparent" />
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10:10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <Star size={18} />
                </div>
                <h3 className="font-display text-xl font-bold">3 Pool Tables</h3>
              </div>
              <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0 text-primary" />
                  <span>Full-size slate-bed tables with professional-grade cloth</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap size={16} className="mt-0.5 shrink-0 text-primary" />
                  <span>Quality cues, racks, and chalk supplied at every table</span>
                </li>
                <li className="flex items-start gap-3">
                  <Monitor size={16} className="mt-0.5 shrink-0 text-primary" />
                  <span>Overhead lighting and 12 large TVs for live sport while you play</span>
                </li>
                <li className="flex items-start gap-3">
                  <Users size={16} className="mt-0.5 shrink-0 text-primary" />
                  <span>Up to 6 players per table — perfect for groups or doubles</span>
                </li>
              </ul>
              <p className="mt-5 text-xs uppercase tracking-wider text-primary">€12 / hour per table</p>
            </div>

            {/* Darts card */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card/40 p-6 md:p-8">
              <div className="absolute right-0 top-0 h-24 w-24 bg-gradient-to-bl from-accent/10 to-transparent" />
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <Trophy size={18} />
                </div>
                <h3 className="font-display text-xl font-bold">3 Dart Boards</h3>
              </div>
              <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0 text-accent" />
                  <span>Tournament-grade bristle boards with staple-free bullseye wiring</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap size={16} className="mt-0.5 shrink-0 text-accent" />
                  <span>Sets of steel-tip darts available — bring your own if you prefer</span>
                </li>
                <li className="flex items-start gap-3">
                  <Music size={16} className="mt-0.5 shrink-0 text-accent" />
                  <span>Located in a dedicated darts snug with great acoustics and vibe</span>
                </li>
                <li className="flex items-start gap-3">
                  <Beer size={16} className="mt-0.5 shrink-0 text-accent" />
                  <span>Bar service to the oche — no need to pause your throw</span>
                </li>
              </ul>
              <p className="mt-5 text-xs uppercase tracking-wider text-accent">€15 / hour per board</p>
            </div>
          </div>

          {/* Extra info strip */}
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              { title: "Book Ahead", desc: "Reserve online to guarantee your table or board at peak times — weekends, match nights, and bank holidays fill fast." },
              { title: "Walk-Ins Welcome", desc: "Plenty of availability on week nights. Just ask at the bar and we’ll get you set up with cues or darts in minutes." },
              { title: "Group Deals", desc: "Birthdays, stags, and work socials — book multiple tables and we’ll sort a drinks package to match." },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-border bg-card/30 p-5">
                <p className="font-display text-base font-bold">{item.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Gallery */}
        <section aria-labelledby="pool-gallery-heading" className="mt-20">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-primary">Inside the room</p>
              <h2 id="pool-gallery-heading" className="mt-2 font-display text-2xl font-bold md:text-3xl">
                Pool &amp; Darts Gallery
              </h2>
            </div>
            <p className="hidden text-xs text-muted-foreground sm:block">Click to enlarge</p>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3">
            {GALLERY.map((img, i) => (
              <button
                key={img.url}
                type="button"
                onClick={() => setLightboxIndex(i)}
                className={`group relative overflow-hidden rounded-xl border border-border bg-card/30 ${
                  i === 0 ? "col-span-2 row-span-2 aspect-[4/3] md:col-span-2 md:row-span-2" : "aspect-square"
                }`}
                aria-label={`Open image: ${img.alt}`}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  loading={i < 2 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "low"}
                  decoding="async"
                  sizes={i === 0 ? "(min-width: 768px) 66vw, 100vw" : "(min-width: 1024px) 33vw, (min-width: 768px) 33vw, 50vw"}
                  width={1600}
                  height={i === 0 ? 1200 : 1600}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                <span className="pointer-events-none absolute bottom-0 left-0 right-0 p-4 text-left text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                  {img.alt}
                </span>
              </button>
            ))}
          </div>
        </section>

        <div id="book" className="mt-12 scroll-mt-24 rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8">
          <h2 className="font-display text-2xl font-bold md:text-3xl">Book your table</h2>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">
            Reserve a pool table for match night, a stag, a birthday or a casual catch-up.
          </p>
          <button
            onClick={() => openModal({ bookingType: "pool_table" })}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground transition-all hover:opacity-90"
          >
            Reserve a Table
          </button>

        </div>
      </div>
      {lightboxIndex !== null && (
        <Lightbox
          images={GALLERY.map((g) => g.url)}
          index={lightboxIndex}
          alt={GALLERY[lightboxIndex]?.alt ?? ""}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}
    <SiteFooter />
    </main>
  );
}
